import { Suspense, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router';

import { useMutation, useQuery } from '@tanstack/react-query';

import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/components/ui/use-toast';
import { MaintenanceBanner } from '@/lib/components/LayoutBanner/MaintenanceBanner';
import Loader from '@/lib/components/Loader';
import { ThemeProvider } from '@/lib/components/theme-provider';

import {
  glacierNewGradApiGetAnswersOptions,
  glacierNewGradApiValidateAgencyMutation,
  glacierNewGradApiValidateAssociationCodeMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type {
  UsStateEnum,
  ValidatedAssociationCodeResponse,
} from '@/lib/heyapi/types.gen';

import { SideBar } from '../SideBar';
import { NewGradPaidContext } from './NewGradPaidContext';

export type AgencyType = {
  name: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  email: string;
  agencyUuid?: string;
  agencyOperatingStates: UsStateEnum[];
};

export const NewGradLayout = () => {
  const params = useLocation();
  const { pk, agency_id } = useParams();
  const isOnPaymentPage = params.pathname.includes('/5');

  const [agencyData, setAgencyData] = useState<AgencyType>({
    name: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    agencyUuid: '',
    agencyOperatingStates: [],
  });

  const [validatedAssociationCode, setValidatedAssociationCode] =
    useState<ValidatedAssociationCodeResponse | null>(null);

  const { data: answersData } = useQuery({
    ...glacierNewGradApiGetAnswersOptions({
      path: {
        account_id: pk!,
      },
    }),
    enabled: Boolean(pk) && !isOnPaymentPage,
  });

  const validateAgency = useMutation({
    ...glacierNewGradApiValidateAgencyMutation(),
    onError: (error) => {
      setAgencyData({
        name: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        agencyUuid: '',
        agencyOperatingStates: [],
      });

      toast({
        title: 'Error validating Agency',
        description: (
          <p className="mt-2 max-h-[40vh] w-[340px] rounded-md bg-red-600 p-4 text-white">
            {JSON.stringify(error)}
          </p>
        ),
      });
    },
    onSuccess: (_data) => {
      setAgencyData({
        name: _data.agencyName || '',
        firstName: _data.firstName || '',
        lastName: _data.lastName || '',
        phone: _data.phone || '',
        email: _data.email || '',
        agencyUuid: _data.agencyUuid || '',
        agencyOperatingStates: _data.agencyOperatingStates,
      });
    },
  });

  const validateAssociationCode = useMutation({
    ...glacierNewGradApiValidateAssociationCodeMutation(),
    onSuccess: (result) => {
      setValidatedAssociationCode(result);
      setAgencyData({
        name: result.agencyName || '',
        firstName: result.firstName || '',
        lastName: result.lastName || '',
        phone: result.phone || '',
        email: result.email || '',
        agencyUuid: result.agencyUuid || '',
        agencyOperatingStates: result.agencyOperatingStates,
      });
    },
  });

  useEffect(() => {
    if (isOnPaymentPage) return;
    const savedCode = answersData?.pages.yourInformation.associationCode;
    const savedState = answersData?.pages.yourInformation.policyContractState;
    const agencyId =
      agency_id || answersData?.pages.yourInformation.agencyUuid || null;

    if (savedCode && savedState) {
      validateAssociationCode.mutate({
        body: {
          agencyUuid: agencyId,
          practiceState: savedState,
          code: savedCode,
        },
      });
    } else if (savedState || agencyId) {
      validateAgency.mutate({
        body: {
          agencyUuid: agencyId,
          practiceState: savedState || null,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate only when BE data or URL changes
  }, [answersData, agency_id, isOnPaymentPage]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- required for cross-browser beforeunload support
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const [canNavigate, setCanNavigate] = useState(false);

  const pageNumber = !pk
    ? params.pathname.split('/')[2]
    : params.pathname.split('/')[3];
  return (
    <ThemeProvider>
      <NewGradPaidContext.Provider
        value={{
          canNavigate,
          setCanNavigate,
        }}
      >
        <MaintenanceBanner />
        <div className="flex flex-col dark:bg-black dark:text-white">
          <div className="min-h-14 bg-brand-dark-green px-4 md:min-h-20 md:px-6">
            <Link
              to={`/newgrad${pk ? `/${pk}` : ''}/1/${agency_id || ''}`}
              key="Home"
              className="my-4 ml-[16px] flex items-center gap-4 md:flex-row md:gap-8"
            >
              <div className="flex flex-col">
                <img
                  src="/assets/aspen_sm.svg"
                  alt="Aspen logo"
                  className="w-16 md:w-24"
                />
              </div>
              <p className="mt-[9px] flex justify-center text-lg font-semibold text-white md:mt-4 md:w-full md:text-2xl">
                New Grad Online Portal
              </p>
            </Link>
          </div>
          <div className="flex flex-col">
            <main className="w-full max-w-full">
              <section className="wrapper mx-auto flex max-w-full items-center justify-between py-2">
                <div className="mt-[18px] flex w-full flex-col justify-center gap-8 md:flex-row">
                  <div className="flex w-full flex-col md:w-4/12 md:max-w-[350px]">
                    <SideBar
                      currentStep={Number(pageNumber)}
                      agencyData={agencyData}
                      canNavigate={canNavigate}
                    />
                  </div>
                  <div className="flex flex-col md:w-8/12">
                    <Suspense fallback={<Loader />}>
                      <Outlet
                        context={{
                          agencyData,
                          setAgencyData,
                          validateAgency,
                          validateAssociationCode,
                          validatedAssociationCode,
                          setValidatedAssociationCode,
                        }}
                      />
                    </Suspense>
                  </div>
                </div>
              </section>
            </main>
            <Toaster />
          </div>
        </div>
      </NewGradPaidContext.Provider>
    </ThemeProvider>
  );
};
